import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Settings, LogOut, Shield, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Avatar } from "./ui-em";

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="profile-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/15"
        style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
      >
        <Avatar name={user.name} size="sm" />
        <div className="hidden sm:block text-left">
          <div className="text-sm text-white leading-tight">{user.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight">{user.role}</div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 ${open ? "rotate-180" : ""}`} style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
      </button>

      {open && (
        <div
          data-testid="profile-menu-dropdown"
          className="absolute right-0 mt-2 w-64 em-card p-2 animate-fade-in z-40"
          style={{ boxShadow: "0 20px 60px -20px rgba(0,0,0,0.7)" }}
        >
          <div className="px-3 py-3 border-b border-white/5 mb-2">
            <div className="flex items-center gap-3">
              <Avatar name={user.name} size="md" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{user.name}</div>
                <div className="text-xs text-zinc-500 truncate">{user.email}</div>
              </div>
            </div>
          </div>
          <MenuItem to="/settings" icon={User} label={t("profile.profile")} onClick={() => setOpen(false)} />
          <MenuItem to="/settings" icon={Settings} label={t("profile.settings")} onClick={() => setOpen(false)} />
          {user.role === "admin" && (
            <MenuItem to="/admin/knowledge" icon={Shield} label={t("profile.adminPanel")} onClick={() => setOpen(false)} />
          )}
          <div className="h-px bg-white/5 my-2" />
          <button
            data-testid="profile-menu-logout"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-400 hover:text-red-300 hover:bg-red-500/[0.08]"
            style={{ transitionProperty: "background-color, color", transitionDuration: "160ms" }}
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            {t("profile.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({ to, icon: Icon, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-300 hover:text-white hover:bg-white/5"
      style={{ transitionProperty: "background-color, color", transitionDuration: "160ms" }}
    >
      <Icon className="w-4 h-4 text-zinc-500" strokeWidth={1.75} />
      {label}
    </Link>
  );
}
